import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  CheckCircle as PaidIcon,
  Cancel as CancelIcon,
  Schedule as DraftIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment } from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { printOrDownloadPdf } from '../utils/printUtils';
import PaymentDialog from './PaymentDialog';

const InvoiceList = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sort invoices when sortOrder changes
  useEffect(() => {
    if (invoices.length > 0) {
      const sortedInvoices = [...invoices].sort((a, b) => {
        const dateA = new Date(a.createdAt || a.issueDate || 0);
        const dateB = new Date(b.createdAt || b.issueDate || 0);
        return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      });
      setInvoices(sortedInvoices);
    }
  }, [sortOrder, invoices.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInvoices = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(API_ENDPOINTS.invoices);
      // Sort invoices by creation date based on sortOrder
      const sortedInvoices = response.data.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.issueDate || 0);
        const dateB = new Date(b.createdAt || b.issueDate || 0);
        return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      });
      setInvoices(sortedInvoices);
    } catch (err) {
      setError('Failed to fetch invoices');
      // Mock data for demonstration
      setInvoices([
        {
          id: 1,
          invoiceNumber: 'INV-1703123456789',
          customer: { name: 'John Doe', phone: '555-0123' },
          vehicle: { make: 'Chevrolet', model: 'Camaro', year: 2020 },
          status: 'PAID',
          totalAmount: 1250.00,
          issueDate: '2024-01-15',
          dueDate: '2024-02-14',
          createdAt: '2024-01-15T10:00:00Z'
        },
        {
          id: 2,
          invoiceNumber: 'INV-1703123456790',
          customer: { name: 'Jane Smith', phone: '555-0456' },
          vehicle: { make: 'Ford', model: 'Mustang', year: 2019 },
          status: 'SENT',
          totalAmount: 890.00,
          issueDate: '2024-01-16',
          dueDate: '2024-02-15',
          createdAt: '2024-01-16T10:00:00Z'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (invoice) => {
    try {
      // Fetch PDF data
      const response = await axios.post(API_ENDPOINTS.invoicePdf(invoice.id), {}, {
        responseType: 'blob'
      });
      
      // Use the utility function to print or download
      const result = await printOrDownloadPdf(
        response.data, 
        `invoice-${invoice.invoiceNumber}.pdf`,
        { showProgress: true }
      );
      
      if (result.success) {
        if (result.method === 'print') {
          console.log('✅ Invoice printed successfully');
        } else {
          console.log('📥 Invoice downloaded successfully');
        }
      } else {
        alert('Failed to print or download invoice');
      }
      
    } catch (err) {
      console.error('Failed to print invoice:', err);
      alert('Failed to print invoice: ' + (err.response?.data || err.message));
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await axios.post(API_ENDPOINTS.invoicePdf(invoice.id), {}, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF');
    }
  };

  const handleEmail = async (invoice) => {
    const emailAddress = prompt('Enter email address to send invoice to:');
    if (!emailAddress) return;
    
    try {
      await axios.post(API_ENDPOINTS.invoiceEmail(invoice.id), {
        emailAddress: emailAddress
      });
      alert('Invoice sent successfully!');
    } catch (err) {
      console.error('Failed to send email:', err);
      alert('Failed to send email');
    }
  };

  const handleStatusChange = async (invoice, newStatus) => {
    // If marking as paid, show payment dialog
    if (newStatus === 'PAID') {
      setSelectedInvoice(invoice);
      setPaymentDialogOpen(true);
      return;
    }

    // For other statuses, update directly
    await updateInvoiceStatus(invoice, newStatus);
  };

  const updateInvoiceStatus = async (invoice, newStatus, paymentDetails = null) => {
    try {
      const requestData = {
        status: newStatus,
        ...paymentDetails
      };

      await axios.patch(API_ENDPOINTS.invoiceStatus(invoice.id), requestData);
      
      // Update the local state to reflect the change
      setInvoices(prevInvoices => 
        prevInvoices.map(inv => 
          inv.id === invoice.id ? { 
            ...inv, 
            status: newStatus,
            paymentType: paymentDetails?.paymentType || inv.paymentType,
            authorizationCode: paymentDetails?.authorizationCode || inv.authorizationCode,
            paymentDate: paymentDetails?.paymentType ? new Date().toISOString() : inv.paymentDate
          } : inv
        )
      );
      
      alert(`Invoice status updated to ${newStatus}`);
    } catch (err) {
      console.error('Failed to update invoice status:', err);
      alert('Failed to update invoice status');
    }
  };

  const handlePaymentConfirm = async (paymentDetails) => {
    if (selectedInvoice) {
      await updateInvoiceStatus(selectedInvoice, 'PAID', paymentDetails);
    }
  };

  // Filter invoices based on status filter and search query
  const filteredInvoices = invoices.filter(invoice => {
    // Status filter
    if (statusFilter !== 'ALL' && invoice.status !== statusFilter) {
      return false;
    }
    
    // Search filter
    if (searchQuery.trim() === '') return true;
    
    const query = searchQuery.toLowerCase();
    
    // Search in customer details
    const customerName = invoice.customer?.name?.toLowerCase() || '';
    const customerPhone = invoice.customer?.phone?.toLowerCase() || '';
    const customerEmail = invoice.customer?.email?.toLowerCase() || '';
    
    // Search in vehicle details
    const vehicleMake = invoice.vehicle?.make?.toLowerCase() || '';
    const vehicleModel = invoice.vehicle?.model?.toLowerCase() || '';
    const vehicleYear = invoice.vehicle?.year?.toString() || '';
    const vin = invoice.vin?.toLowerCase() || '';
    
    // Search in invoice details
    const invoiceNumber = invoice.invoiceNumber?.toLowerCase() || '';
    
    return customerName.includes(query) ||
           customerPhone.includes(query) ||
           customerEmail.includes(query) ||
           vehicleMake.includes(query) ||
           vehicleModel.includes(query) ||
           vehicleYear.includes(query) ||
           vin.includes(query) ||
           invoiceNumber.includes(query);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Invoices
        </Typography>
                 <Button
           variant="contained"
           startIcon={<AddIcon />}
           onClick={() => navigate('/')}
         >
           Create New Invoice
         </Button>
       </Box>

               {/* Search and Filter Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
          {/* Search Input */}
          <TextField
            size="small"
            placeholder="Search by customer, vehicle, or invoice number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          
                     {/* Status Filter */}
           <FormControl size="small" sx={{ minWidth: 200 }}>
             <InputLabel>Filter by Status</InputLabel>
             <Select
               value={statusFilter}
               label="Filter by Status"
               onChange={(e) => setStatusFilter(e.target.value)}
             >
               <MenuItem value="ALL">All Statuses</MenuItem>
               <MenuItem value="DRAFT">Draft</MenuItem>
               <MenuItem value="PAID">Paid</MenuItem>
               <MenuItem value="CANCELLED">Cancelled</MenuItem>
             </Select>
           </FormControl>
           
           {/* Sort Order */}
           <FormControl size="small" sx={{ minWidth: 200 }}>
             <InputLabel>Sort by Date</InputLabel>
             <Select
               value={sortOrder}
               label="Sort by Date"
               onChange={(e) => setSortOrder(e.target.value)}
             >
               <MenuItem value="newest">Newest First</MenuItem>
               <MenuItem value="oldest">Oldest First</MenuItem>
             </Select>
           </FormControl>
        </Box>

                     {/* Results Count */}
                 <Box sx={{ mb: 2 }}>
           <Typography variant="body2" color="textSecondary">
             Showing {filteredInvoices.length} of {invoices.length} invoices
             {statusFilter !== 'ALL' && ` (filtered by ${statusFilter})`}
             {searchQuery.trim() !== '' && ` (searching for "${searchQuery}")`}
             {` (sorted by ${sortOrder === 'newest' ? 'newest first' : 'oldest first'})`}
           </Typography>
         </Box>

               {filteredInvoices.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {invoices.length === 0 ? 'No invoices found' : 
                 searchQuery.trim() !== '' ? 'No invoices match your search' : 'No invoices match the filter'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {invoices.length === 0 ? 'Create your first invoice to get started' : 
                 searchQuery.trim() !== '' ? 'Try adjusting your search terms or clearing the search' : 'Try changing the status filter'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
        <TableContainer component={Paper}>
          <Table>
                         <TableHead>
               <TableRow>
                 <TableCell>Invoice #</TableCell>
                 <TableCell>Customer</TableCell>
                 <TableCell>Vehicle</TableCell>
                 <TableCell>Status</TableCell>
                 <TableCell>Total</TableCell>
                 <TableCell>Created</TableCell>
                 <TableCell>Actions</TableCell>
               </TableRow>
             </TableHead>
                         <TableBody>
               {filteredInvoices.map(invoice => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {invoice.invoiceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {invoice.customer?.name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {invoice.customer?.phone || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {invoice.vehicle?.year} {invoice.vehicle?.make} {invoice.vehicle?.model}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        VIN: {invoice.vin}
                      </Typography>
                    </Box>
                  </TableCell>
                                     <TableCell>
                     <Chip
                       label={invoice.status}
                       color={
                         invoice.status === 'PAID' ? 'success' : 
                         invoice.status === 'SENT' ? 'warning' : 
                         invoice.status === 'DRAFT' ? 'info' :
                         invoice.status === 'CANCELLED' ? 'error' : 'default'
                       }
                       size="small"
                     />
                   </TableCell>
                   <TableCell>
                     <Typography variant="body2" fontWeight="bold" color="primary">
                       ${invoice.totalAmount ? parseFloat(invoice.totalAmount).toFixed(2) : '0.00'}
                     </Typography>
                   </TableCell>
                   <TableCell>
                     <Typography variant="body2">
                       {new Date(invoice.createdAt).toLocaleDateString()}
                     </Typography>
                   </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {/* Status Change Icons */}
                      <Tooltip title="Mark as Paid">
                        <IconButton
                          size="small"
                          onClick={() => handleStatusChange(invoice, 'PAID')}
                          color="success"
                          disabled={invoice.status === 'PAID'}
                          sx={{ 
                            bgcolor: invoice.status === 'PAID' ? 'success.light' : 'transparent',
                            '&:hover': { bgcolor: 'success.light' }
                          }}
                        >
                          <PaidIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      
                      
                      <Tooltip title="Mark as Draft">
                        <IconButton
                          size="small"
                          onClick={() => handleStatusChange(invoice, 'DRAFT')}
                          color="info"
                          disabled={invoice.status === 'DRAFT'}
                          sx={{ 
                            bgcolor: invoice.status === 'DRAFT' ? 'info.light' : 'transparent',
                            '&:hover': { bgcolor: 'info.light' }
                          }}
                        >
                          <DraftIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Mark as Cancelled">
                        <IconButton
                          size="small"
                          onClick={() => handleStatusChange(invoice, 'CANCELLED')}
                          color="error"
                          disabled={invoice.status === 'CANCELLED'}
                          sx={{ 
                            bgcolor: invoice.status === 'CANCELLED' ? 'error.light' : 'transparent',
                            '&:hover': { bgcolor: 'error.light' }
                          }}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {/* Divider */}
                      <Box sx={{ width: '1px', height: '24px', bgcolor: 'divider', mx: 0.5 }} />
                      
                      {/* Action Icons */}
                      <Tooltip title="View/Edit Invoice">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/invoice/${invoice.id}`)}
                          color="primary"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Print Invoice">
                        <IconButton
                          size="small"
                          onClick={() => handlePrint(invoice)}
                          color="primary"
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Email Invoice">
                        <IconButton
                          size="small"
                          onClick={() => handleEmail(invoice)}
                          color="secondary"
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Download PDF">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadPdf(invoice)}
                          color="success"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onConfirm={handlePaymentConfirm}
        invoice={selectedInvoice}
      />
    </Box>
  );
};

export default InvoiceList;
